import React, { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';
import he from '../translations/he';
import en from '../translations/en';
import ar from '../translations/ar';
import ru from '../translations/ru';

const LanguageContext = createContext();

export const useLanguage = () => useContext(LanguageContext);

// Central registry — adding a language only requires a translations file and an entry here.
export const LANGUAGES = [
    { code: 'he', nativeName: 'עברית', dir: 'rtl', locale: 'he-IL' },
    { code: 'en', nativeName: 'English', dir: 'ltr', locale: 'en-US' },
    { code: 'ar', nativeName: 'العربية', dir: 'rtl', locale: 'ar' },
    { code: 'ru', nativeName: 'Русский', dir: 'ltr', locale: 'ru-RU' },
];

const DEFAULT_LANG = 'he';

const allTranslations = { he, en, ar, ru };

const isSupported = (code) => LANGUAGES.some(l => l.code === code);

function getNestedValue(obj, path) {
    return path.split('.').reduce((acc, key) => acc?.[key], obj);
}

function detectInitialLang() {
    try {
        const saved = localStorage.getItem('emailLion_lang');
        if (saved && isSupported(saved)) return saved;
        // Auto-detect from browser language; default to Hebrew if no match
        const browserLang = (navigator.language || navigator.userLanguage || '').toLowerCase();
        const match = LANGUAGES.find(l => browserLang.startsWith(l.code));
        return match ? match.code : DEFAULT_LANG;
    } catch {
        return DEFAULT_LANG;
    }
}

export const LanguageProvider = ({ children }) => {
    const [lang, setLangState] = useState(detectInitialLang);

    const langMeta = useMemo(
        () => LANGUAGES.find(l => l.code === lang) || LANGUAGES[0],
        [lang]
    );

    const setLang = useCallback((code) => {
        if (!isSupported(code)) return;
        setLangState(code);
        try { localStorage.setItem('emailLion_lang', code); } catch { /* ignore */ }
    }, []);

    // Kept for backwards compatibility: cycles to the next language in the list.
    const toggleLanguage = useCallback(() => {
        const idx = LANGUAGES.findIndex(l => l.code === lang);
        const next = LANGUAGES[(idx + 1) % LANGUAGES.length].code;
        setLang(next);
    }, [lang, setLang]);

    const currentTranslations = useMemo(() => allTranslations[lang] || allTranslations[DEFAULT_LANG], [lang]);

    const t = useCallback((key) => {
        const val = getNestedValue(currentTranslations, key);
        if (val !== undefined) return val;
        // Fallback chain: English → Hebrew → raw key
        const enVal = getNestedValue(allTranslations.en, key);
        if (enVal !== undefined) return enVal;
        const heVal = getNestedValue(allTranslations.he, key);
        if (heVal !== undefined) return heVal;
        return key;
    }, [currentTranslations]);

    const isRtl = langMeta.dir === 'rtl';
    const locale = langMeta.locale;

    useEffect(() => {
        document.documentElement.dir = langMeta.dir;
        document.documentElement.lang = lang;
    }, [lang, langMeta]);

    const value = useMemo(() => ({
        lang, setLang, toggleLanguage, t, isRtl, locale, languages: LANGUAGES,
    }), [lang, setLang, toggleLanguage, t, isRtl, locale]);

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};

export default LanguageContext;
